import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Award, Headphones, KeyRound, Zap } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const MOBILE_TRUST = [
  { icon: Zap, label: "Livraison rapide" },
  { icon: KeyRound, label: "Clés officielles" },
  { icon: Headphones, label: "Support 24/7" },
  { icon: Award, label: "Remboursement" },
];

function MobileMediaLink({ link, testId }) {
  if (!link) return null;
  const external = /^https?:\/\//i.test(link);
  const cls = "absolute inset-0 z-[5] block";
  if (external) return <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls} aria-label="Ouvrir le lien" />;
  return <Link to={link} data-testid={testId} className={cls} aria-label="Ouvrir le lien" />;
}

export function MobileHero({ slides = [] }) {
  const [index, setIndex] = useState(0);
  const [trust, setTrust] = useState(null);
  const count = slides.length;

  useEffect(() => {
    let active = true;
    api.get("/settings/hero-trust")
      .then(({ data }) => active && setTrust(data))
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (count < 2) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (!count) return <div className="hero-shell h-64 w-full" />;
  const slide = slides[index];
  const isBg = slide.layout === "background";
  const showText = !isBg || slide.show_text !== false;
  const showButton = slide.show_button !== false;
  const trustEnabled = trust ? trust.enabled !== false : true;
  const trustItems = (trust?.items?.length ? trust.items : MOBILE_TRUST).map((item, i) => ({
    label: item.label || `${item.title || ""} ${item.subtitle || ""}`.trim(),
    icon: MOBILE_TRUST[i % MOBILE_TRUST.length].icon,
  }));

  return (
    <section data-testid="mobile-hero" className="hero-shell relative overflow-hidden rounded-b-[28px] px-4 pb-5 pt-6">
      {isBg && (slide.video_url || slide.image_mobile_url || slide.image_url) && (
        <>
          {slide.video_url ? (
            <video
              src={fileUrl(slide.video_url)}
              autoPlay
              muted
              loop
              playsInline
              data-testid="mobile-hero-bg-video"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <img
              src={fileUrl(slide.image_mobile_url || slide.image_url)}
              alt=""
              data-testid="mobile-hero-bg-image"
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {slide.darken && (
            <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/80 via-[#020617]/55 to-[#020617]/40" />
          )}
          <MobileMediaLink link={slide.image_link} testId="mobile-hero-bg-link" />
        </>
      )}
      {!isBg && <div className="hero-grid-lines pointer-events-none absolute inset-0 opacity-60" />}
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -24 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className={`relative z-10 ${isBg && slide.image_link ? "pointer-events-none" : ""}`}
        >
          {showText && slide.badge && (
            <span className="mb-2 inline-block rounded-full bg-rose-500 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-white">
              {slide.badge}
            </span>
          )}
          {showText && (
            <>
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-brand">
            {slide.mobile_title || "ISMO-TECH"}
          </p>
          <h1 className="mt-2 font-display text-[26px] font-extrabold leading-[1.12] text-white">
            {slide.title} <span className="text-brand">{slide.highlight}</span>
          </h1>
          <p className="mt-2 text-[13px] leading-snug text-slate-300">{slide.mobile_text || slide.text}</p>
            </>
          )}

          {!isBg && (
          <div className="mt-4 flex items-end gap-2 overflow-hidden">
            {(slide.products?.length ? slide.products : [slide.image_url]).filter(Boolean).slice(0, 4).map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                loading="lazy"
                className="h-[74px] w-auto rounded-lg bg-white/95 object-contain p-1 shadow-[0_12px_30px_rgba(0,82,255,0.4)]"
                style={{ transform: `translateY(${i % 2 === 0 ? 0 : -6}px)` }}
              />
            ))}
          </div>
          )}

          <div className={`pointer-events-auto mt-5 flex items-center gap-3 ${isBg && !showButton ? "justify-end" : ""}`}>
            {showButton && (
            <Link
              to={slide.button_link || "/boutique"}
              data-testid="mobile-hero-cta"
              className="btn-glow flex-1 rounded-full bg-brand px-5 py-3 text-center text-sm font-bold text-white active:scale-95"
            >
              {slide.button_text || "Acheter maintenant"}
            </Link>
            )}
            <div className="flex items-center gap-1.5">
              {slides.map((s, i) => (
                <button
                  key={s.id || i}
                  aria-label={`Slide ${i + 1}`}
                  data-testid={`mobile-hero-dot-${i}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-6 bg-brand" : "w-1.5 bg-white/30"
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {!isBg && trustEnabled && (
      <div className="relative z-10 mt-5 grid grid-cols-4 gap-1 border-t border-white/10 pt-4" data-testid="mobile-hero-trust-row">
        {trustItems.map(({ icon: Icon, label }) => (
          <div key={label} className="flex flex-col items-center gap-1 text-center">
            <Icon className="h-[18px] w-[18px] text-brand" strokeWidth={2} />
            <span className="text-[9.5px] font-semibold leading-tight text-slate-300">{label}</span>
          </div>
        ))}
      </div>
      )}
    </section>
  );
}

export default MobileHero;
